import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  CalendarDays,
  Loader2,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Inbox,
} from "lucide-react";

function formatLong(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function MySchedule() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = (n) => {
    setLoading(true);
    api
      .get("/me/schedule", { params: { days: n } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
  }, [days]);

  const grouped = (() => {
    if (!data?.items) return [];
    const map = new Map();
    for (const it of data.items) {
      const arr = map.get(it.date) || [];
      arr.push(it);
      map.set(it.date, arr);
    }
    return Array.from(map.entries());
  })();

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="my-schedule-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Upcoming</div>
          <h1 className="mt-1 text-2xl font-heading font-semibold">My schedule</h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Visits based on each client's scheduled days. Completed visits show a check.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-clinical-border bg-white p-1">
          {[7, 14, 30].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              data-testid={`schedule-range-${n}`}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                days === n
                  ? "bg-clinical-primary text-white"
                  : "text-clinical-subtext hover:bg-clinical-bg"
              }`}
            >
              {n} days
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading schedule…
        </div>
      ) : !data?.items?.length ? (
        <div className="card-surface p-10 text-center" data-testid="schedule-empty">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-clinical-bg text-clinical-subtext">
            <Inbox className="h-6 w-6" />
          </span>
          <h3 className="mt-3 font-heading text-lg">Nothing scheduled</h3>
          <p className="mt-1 text-sm text-clinical-subtext">
            No assigned visits in the next {days} days.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([d, items]) => (
            <div key={d} className="card-surface p-4" data-testid={`schedule-day-${d}`}>
              <div className="mb-3 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-clinical-primary" />
                <h2 className="font-heading text-sm font-medium">
                  {formatLong(d)}
                  {d === todayIso && (
                    <span className="ml-2 pill bg-clinical-primary/10 text-clinical-primary">
                      Today
                    </span>
                  )}
                </h2>
              </div>
              <ul className="divide-y divide-clinical-border">
                {items.map((it) => (
                  <li
                    key={`${it.date}-${it.client_id}`}
                    className="flex items-center gap-3 py-2.5"
                    data-testid={`schedule-visit-${it.client_id}-${it.date}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-clinical-text truncate">
                        {it.client_name}
                      </div>
                      {it.client_address && (
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-clinical-subtext">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{it.client_address}</span>
                        </div>
                      )}
                    </div>
                    {it.completed_report_id ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/my-reports`)}
                        className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium"
                        data-testid={`schedule-done-${it.client_id}-${it.date}`}
                      >
                        <CheckCircle2 className="h-4 w-4" /> Completed
                      </button>
                    ) : (
                      <Link
                        to={`/shift/${it.client_id}`}
                        className="btn-secondary"
                        data-testid={`schedule-start-${it.client_id}-${it.date}`}
                      >
                        Start <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
